import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ApiError, PromotionRestrictions } from '../api';
import { promotionEndpoints } from '../api';
import { Button, ErrorNote, Field, Panel, formatWhen } from '../ui';
import { humanize } from './event/shared';

/**
 * What a promo code may touch (plan-promotions.md §1, D-377): the games, the
 * ticket types and price zones, and the channels. The API has taken these
 * since D-143; the form never offered them.
 *
 * Ticket types and zones are chosen **by code** ("GA", "ADULT"), which holds
 * on every game a season build copied, rather than by the id of one event's
 * row. Nothing ticked on a line means that line does not restrict. The
 * id-based lists and the customer list an older client may have set are kept
 * exactly as they are.
 */
export function PromoCodeRestrictions({ promotionId, restrictions, onChanged }: { promotionId: string; restrictions: PromotionRestrictions | null; onChanged: () => void }) {
  const choices = useQuery({ queryKey: ['promotion-restriction-choices'], queryFn: promotionEndpoints.restrictionChoices });
  const [performances, setPerformances] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [zones, setZones] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!restrictions) return;
    setPerformances(restrictions.performance_ids);
    setTypes(restrictions.ticket_type_codes ?? []);
    setZones(restrictions.price_zone_codes ?? []);
    setChannels(restrictions.channels);
  }, [restrictions]);

  const save = useMutation({
    mutationFn: () =>
      promotionEndpoints.setRestrictions(promotionId, {
        ticket_type_ids: restrictions?.ticket_type_ids ?? [],
        section_ids: restrictions?.section_ids ?? [],
        price_zone_ids: restrictions?.price_zone_ids ?? [],
        customer_subs: restrictions?.customer_subs ?? [],
        performance_ids: performances,
        ticket_type_codes: types,
        price_zone_codes: zones,
        channels,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      onChanged();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const toggle = (list: string[], set: (next: string[]) => void, value: string) => {
    setSaved(false);
    set(list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value]);
  };
  const legacy = (restrictions?.ticket_type_ids.length ?? 0) + (restrictions?.price_zone_ids.length ?? 0) + (restrictions?.section_ids.length ?? 0);
  // Games already chosen that are no longer upcoming are kept, and listed by id.
  const known = new Set((choices.data?.performances ?? []).map((p) => p.id));
  const past = performances.filter((id) => !known.has(id));

  return (
    <Panel title="Where it can be used" description="Leave a line unticked to leave it open. A code restricted to games works only on those games; to ticket types or zones, only on those tickets.">
      <div className="space-y-4 border-t border-line px-5 py-4 text-sm">
        {error ? <ErrorNote>{error}</ErrorNote> : null}
        <Field label="Games" htmlFor="restrict-games" hint={performances.length === 0 ? 'Any game' : `${performances.length} chosen`}>
          <div id="restrict-games" className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-line p-2">
            {(choices.data?.performances ?? []).map((performance) => (
              <label key={performance.id} className="flex items-center gap-2">
                <input type="checkbox" checked={performances.includes(performance.id)} onChange={() => toggle(performances, setPerformances, performance.id)} />
                <span>
                  {performance.title} <span className="text-ink-500">· {formatWhen(performance.starts_at)}</span>
                </span>
              </label>
            ))}
            {past.map((id) => (
              <label key={id} className="flex items-center gap-2 text-ink-500">
                <input type="checkbox" checked onChange={() => toggle(performances, setPerformances, id)} />
                <span>A game already played ({id.slice(0, 8)})</span>
              </label>
            ))}
          </div>
        </Field>
        <CodeLine label="Ticket types" values={choices.data?.ticket_type_codes ?? []} chosen={types} onToggle={(code) => toggle(types, setTypes, code)} />
        <CodeLine label="Price zones" values={choices.data?.price_zone_codes ?? []} chosen={zones} onToggle={(code) => toggle(zones, setZones, code)} />
        <CodeLine label="Channels" values={(choices.data?.channels ?? []).map((code) => ({ code, name: humanize(code) }))} chosen={channels} onToggle={(code) => toggle(channels, setChannels, code)} />
        {legacy > 0 ? <p className="text-xs text-ink-500">Also restricted to {legacy} specific ticket type, zone or section row(s) set through the API. Those are kept as they are.</p> : null}
        <div className="flex items-center gap-3">
          <Button variant="primary" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save where it can be used'}
          </Button>
          {saved ? <span className="text-xs text-ink-500">Saved.</span> : null}
        </div>
      </div>
    </Panel>
  );
}

function CodeLine({ label, values, chosen, onToggle }: { label: string; values: { code: string; name: string }[]; chosen: string[]; onToggle: (code: string) => void }) {
  const all = [...values, ...chosen.filter((code) => !values.some((value) => value.code === code)).map((code) => ({ code, name: code }))];
  return (
    <fieldset>
      <legend className="mb-1 font-medium">
        {label} <span className="font-normal text-ink-500">{chosen.length === 0 ? '· any' : `· ${chosen.length} chosen`}</span>
      </legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {all.map((value) => (
          <label key={value.code} className="flex items-center gap-1.5">
            <input type="checkbox" checked={chosen.includes(value.code)} onChange={() => onToggle(value.code)} />
            <span>
              {value.name} <span className="font-mono text-xs text-ink-500">{value.code}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
